import { Button, Checkbox, Combobox, Currency, Form, Modal } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { REGISSEUR, TYPES_PRODUIT } from '@/constants';
import { ProduitContext } from '@/contexts';
import { deleteProduit, postProduit, putProduit } from '@/routes';
import type { ErrorFieldType, ProduitType } from '@/types';
import { computePrixHT, computePrixTTC } from '@/utils';
import { parseApiValidationErrors } from '@/utils/apiErrors';
import { useRegieStore, useRegisseurStore } from '@/zustand';
import ProduitsListeDetail from './ProduitsListeDetail';

type ModalDetailProduitProps = {
  // Produit à partir du quel on a ouvert la modal (undefined = création)
  produit?: ProduitType;
  onClose: () => void;
  onSave: () => void;
};

const ModalDetailProduit = (props: ModalDetailProduitProps) => {
  const { produit, onClose, onSave } = props;

  const autonome = useRegieStore((state) => state.regie?.autonome);
  const mandat = useRegisseurStore((state) => state.regisseur?.mandat);
  const canEdit = autonome && mandat === REGISSEUR.TYPE_PRINCIPAL;
  const isCreation = _.isNil(produit);

  const [libelle, setLibelle] = React.useState(produit?.libelle ?? '');
  const [code, setCode] = React.useState(produit?.code ?? '');
  const [tva, setTva] = React.useState(produit?.tva ?? 0);
  const [prixHT, setPrixHT] = React.useState<number | null>(produit?.prixUnitaireHT ?? null);
  const [prixTTC, setPrixTTC] = React.useState<number | null>(
    produit?.prixUnitaireHT != null
      ? computePrixTTC(produit.prixUnitaireHT, produit.tva ?? 0)
      : null,
  );
  const [payableCESU, setPayableCESU] = React.useState(produit?.payableCESU ?? false);
  const [payableChequeVacance, setPayableChequeVacance] = React.useState(
    produit?.payableChequeVacance ?? false,
  );
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [errors, setErrors] = React.useState<ErrorFieldType[]>([]);

  const { produits: contextProduits } = React.useContext(ProduitContext);

  // State local des imputations en mode création
  const [localImputations, setLocalImputations] = React.useState<ProduitType[]>([]);

  // Saisie bidirectionnelle : HT ↔ TTC via le taux de TVA (règle HT + HT * tva/100 = TTC).
  // Le composant Currency renvoie un nombre (ou null) sur onBlur.
  const handleChangePrixHT = (value: number | null) => {
    setPrixHT(value);
    setPrixTTC(value == null ? null : computePrixTTC(value, tva));
  };

  const handleChangePrixTTC = (value: number | null) => {
    setPrixTTC(value);
    setPrixHT(value == null ? null : computePrixHT(value, tva));
  };

  const handleChangeTva = (value: number) => {
    setTva(value);
    setPrixTTC(prixHT == null ? null : computePrixTTC(prixHT, value));
  };

  // Code produit : 6 caractères alphanumériques en majuscules (même règle que le code interne
  // de la régie, cf. ConstitutionRegie/Fiche/Panel/Identification.tsx).
  const handleChangeCode = (value: string) => {
    if (!/^[a-zA-Z0-9]*$/.test(value)) {
      return;
    }

    setCode(_.toUpper(value));
  };

  // Le libellé et le code sont obligatoires pour un produit au comptant
  const isValid = !_.isEmpty(libelle) && !_.isEmpty(code);

  const hasChanges = isCreation
    ? !_.isEmpty(libelle)
    : libelle !== (produit?.libelle ?? '') ||
      code !== (produit?.code ?? '') ||
      (prixHT ?? 0) !== (produit?.prixUnitaireHT ?? 0) ||
      tva !== (produit?.tva ?? 0) ||
      payableCESU !== (produit?.payableCESU ?? false) ||
      payableChequeVacance !== (produit?.payableChequeVacance ?? false);

  const tvaOptions = React.useMemo(() => {
    const taux = [0, 5.5, 10, 20];

    const options = taux.map((t) => ({
      key: `tva-${t}`,
      value: t,
      text: `${t}%`,
    }));

    // Si la valeur actuelle n'est pas dans les taux génériques, on l'ajoute
    if (!taux.includes(tva)) {
      options.unshift({ key: `tva-${tva}`, value: tva, text: `${tva}%` });
    }

    return options;
  }, [tva]);

  // Handlers locaux pour les imputations en mode création
  const handleImputationAdd = React.useCallback((item) => {
    setLocalImputations((prev) => [item, ...prev]);
    return true;
  }, []);

  const handleImputationEdit = React.useCallback((item) => {
    setLocalImputations((prev) =>
      _.map(prev, (i) => (i.identifiant === item.identifiant ? item : i)),
    );
    return true;
  }, []);

  const handleImputationDelete = React.useCallback((item) => {
    setLocalImputations((prev) => _.filter(prev, (i) => i.identifiant !== item.identifiant));
    return true;
  }, []);

  const handleApiError = React.useCallback((error: any, defaultMessage: string) => {
    const apiErrors = parseApiValidationErrors(error?.response?.data ?? error?.message);

    if (!_.isEmpty(apiErrors)) {
      setErrors(apiErrors);
    }

    // Le doublon de libellé au comptant remonte un message métier explicite
    // (cf. ProduitController::assertLibelleUniqueForComptant) : on l'affiche tel quel,
    // les autres erreurs conservent le message générique.
    const erreurLibelle = _.find(apiErrors, { id: 'libelle' });

    toast(erreurLibelle?.message ?? defaultMessage, { type: 'error' });
  }, []);

  const handleSave = React.useCallback(() => {
    setErrors([]);
    setSaving(true);

    if (isCreation) {
      const baseFields = {
        libelle,
        code,
        prixUnitaireHT: prixHT,
        tva,
        payableCESU,
        payableChequeVacance,
        type: TYPES_PRODUIT.AU_COMPTANT,
      };

      const promises = _.isEmpty(localImputations)
        ? [request.post(postProduit(), baseFields)]
        : _.map(localImputations, (imputation) =>
            request.post(postProduit(), { ...imputation, ...baseFields }),
          );

      Promise.all(promises)
        .then(() => {
          toast('Produit créé avec succès', { type: 'success' });
          onSave();
        })
        .catch((error) => {
          handleApiError(error, 'Erreur lors de la création du produit');
        })
        .finally(() => {
          setSaving(false);
        });

      return;
    }

    request
      .put(putProduit(produit.identifiant), {
        ...produit,
        libelle,
        code,
        prixUnitaireHT: prixHT,
        tva,
        payableCESU,
        payableChequeVacance,
        type: TYPES_PRODUIT.AU_COMPTANT,
      })
      .then(() => {
        toast('Produit modifié avec succès', { type: 'success' });
        onSave();
      })
      .catch((error) => {
        handleApiError(error, 'Erreur lors de la modification du produit');
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    isCreation,
    produit,
    localImputations,
    libelle,
    code,
    prixHT,
    tva,
    payableCESU,
    payableChequeVacance,
    onSave,
    handleApiError,
  ]);

  const handleDelete = React.useCallback(() => {
    setDeleting(true);

    const promises = _.map(contextProduits, (p) => request.delete(deleteProduit(p.identifiant)));

    Promise.all(promises)
      .then(() => {
        toast('Produit supprimé avec succès', { type: 'success' });
        onSave();
      })
      .catch(() => {
        toast('Erreur lors de la suppression du produit', { type: 'error' });
      })
      .finally(() => {
        setDeleting(false);
      });
  }, [contextProduits, onSave]);

  const modalHeader = React.useMemo(() => {
    if (isCreation) return "Ajout d'un nouveau produit";
    if (canEdit) return 'Modification du produit';
    if (autonome) return produit?.libelle;
    return <>{produit?.libelle} - Imputations détaillées et informations budgétaires</>;
  }, [isCreation, canEdit, autonome, produit]);

  return (
    <Modal closeIcon open size="huge" onClose={onClose}>
      <Modal.Header>{modalHeader}</Modal.Header>
      <Modal.Content>
        {(canEdit || isCreation) && (
          <Form
            // @ts-expect-error
            style={{ marginBottom: 16 }}
          >
            <div style={{ maxWidth: 450 }}>
              <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-end' }}>
                <div style={{ width: 120 }}>
                  <Form.Input
                    fluid
                    required
                    label="Code"
                    maxLength={6}
                    value={code}
                    error={_.some(errors, { id: 'code' })}
                    onChange={(e, { value }) => handleChangeCode(value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Form.Input
                    fluid
                    required
                    label="Libellé"
                    value={libelle}
                    error={_.some(errors, { id: 'libelle' })}
                    onChange={(e, { value }) => setLibelle(value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1.5em', marginTop: 16 }}>
                <Form.Field
                  required
                  error={_.some(errors, { id: 'prixUnitaireHT' })}
                  // @ts-expect-error
                  style={{ width: 150 }}
                >
                  <label>Prix unitaire HT</label>
                  <Currency
                    data-testid="prix-unitaire-ht"
                    value={prixHT}
                    precision={2}
                    valueMin={0}
                    devise
                    description=""
                    onBlur={(e, value) => handleChangePrixHT(value)}
                  />
                </Form.Field>
                <Form.Field
                  // @ts-expect-error
                  style={{ flex: 1 }}
                >
                  <label>Taux de TVA</label>
                  {/* @ts-expect-error */}
                  <Combobox
                    search
                    selection
                    allowAdditions
                    additionLabel=""
                    options={tvaOptions}
                    value={tva}
                    onChange={(e, { value }) => handleChangeTva(Number(value))}
                    onAddItem={(e, { value }) => handleChangeTva(Number(value))}
                  />
                </Form.Field>
                <Form.Field
                  required
                  // @ts-expect-error
                  style={{ width: 150 }}
                >
                  <label>Prix unitaire TTC</label>
                  <Currency
                    data-testid="prix-unitaire-ttc"
                    value={prixTTC}
                    precision={2}
                    valueMin={0}
                    devise
                    description=""
                    onBlur={(e, value) => handleChangePrixTTC(value)}
                  />
                </Form.Field>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1.5em', alignItems: 'center', marginTop: 16 }}>
              <label style={{ fontWeight: 'bold' }}>Payable en :</label>
              <Checkbox
                fitted
                label="CESU"
                checked={payableCESU}
                onChange={(e, { checked }) => setPayableCESU(checked)}
              />
              <Checkbox
                fitted
                label="Chèque vacance"
                checked={payableChequeVacance}
                onChange={(e, { checked }) => setPayableChequeVacance(checked)}
              />
            </div>
          </Form>
        )}
        <ProduitsListeDetail
          editable={isCreation || !!canEdit}
          produit={isCreation ? undefined : produit}
          {...(isCreation && {
            localItems: localImputations,
            onLocalAdd: handleImputationAdd,
            onLocalEdit: handleImputationEdit,
            onLocalDelete: handleImputationDelete,
          })}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-close"
          onClick={onClose}
          basic
          size="tiny"
          icon="close"
          content="Annuler"
        />
        {canEdit && !isCreation && (
          <Button
            data-testid="button-delete"
            negative
            icon="trash"
            content="Supprimer le produit"
            loading={deleting}
            disabled={deleting || saving}
            onClick={handleDelete}
          />
        )}
        {(canEdit || isCreation) && (
          <Button
            data-testid="button-save"
            positive
            icon="save"
            content="Enregistrer"
            loading={saving}
            disabled={saving || deleting || !hasChanges || !isValid}
            onClick={handleSave}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default ModalDetailProduit;
